/**
 * AgentFloatingWindow — SAGAX Agent 全局浮动小窗
 *
 * 在 TerminalShell 顶层挂载，覆盖所有平台模块。
 * - 可拖动（标题栏 mousedown）
 * - 可折叠为右下浮动按钮（💬 圆形 48×48）
 * - 持久化位置 + 折叠状态到 localStorage
 * - 在以下路径自动隐藏，避免与全屏 / 登录页冲突：
 *     /sagax-agent (全屏 chat 自己已在), /login, /register, /login/wechat/callback
 */

import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Maximize2, Minus, MessageSquare, X } from 'lucide-react';

const ChatPage = lazy(() => import('../modules/sagax-agent/ChatPage'));

const STORAGE_KEY_POS = 'sagax_agent_float_pos';
const STORAGE_KEY_COLLAPSED = 'sagax_agent_float_collapsed';

const WIN_WIDTH = 380;
const WIN_HEIGHT = 540;
const FAB_SIZE = 48;
const MARGIN = 16;

// 在这些路径下不显示浮动窗（要么已经全屏 chat，要么是未登录页面）
const HIDDEN_PATH_PREFIXES = [
  '/sagax-agent',
  '/login',
  '/register',
];

const isHiddenRoute = (pathname) => HIDDEN_PATH_PREFIXES.some(p => pathname.startsWith(p));

const clampToViewport = (left, top, w, h) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    left: Math.max(MARGIN, Math.min(left, vw - w - MARGIN)),
    top:  Math.max(MARGIN, Math.min(top,  vh - h - MARGIN)),
  };
};

const loadPos = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_POS);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p?.left !== 'number' || typeof p?.top !== 'number') return null;
    return p;
  } catch { return null; }
};

const defaultPos = () => ({
  left: window.innerWidth - WIN_WIDTH - MARGIN,
  top: window.innerHeight - WIN_HEIGHT - 80, // 80 留给底部 StatusBar
});

const AgentFloatingWindow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const hidden = isHiddenRoute(location.pathname);

  // 首次渲染先用 SSR 安全的默认值（避免 window 引用），mount 后再 hydrate
  const [pos, setPos] = useState({ left: -9999, top: -9999 });
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY_COLLAPSED) === '1'; } catch { return false; }
  });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, origLeft: 0, origTop: 0 });

  // mount 后初始化位置（依赖 window）
  useEffect(() => {
    const saved = loadPos();
    const initial = saved || defaultPos();
    const clamped = clampToViewport(initial.left, initial.top, WIN_WIDTH, WIN_HEIGHT);
    setPos(clamped);
  }, []);

  // window resize 时重新 clamp，避免窗口跑到视口外
  useEffect(() => {
    const onResize = () => {
      setPos(p => clampToViewport(p.left, p.top, WIN_WIDTH, WIN_HEIGHT));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 拖拽
  const onDragStart = useCallback((e) => {
    if (e.button !== 0) return; // 只响应左键
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origLeft: pos.left,
      origTop: pos.top,
    };
    setDragging(true);
  }, [pos.left, pos.top]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      const next = clampToViewport(
        dragRef.current.origLeft + dx,
        dragRef.current.origTop + dy,
        WIN_WIDTH,
        WIN_HEIGHT,
      );
      setPos(next);
    };
    const onUp = () => {
      setDragging(false);
      // 持久化最终位置
      setPos(p => {
        try { localStorage.setItem(STORAGE_KEY_POS, JSON.stringify(p)); } catch { /* quota */ }
        return p;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  const toggleCollapsed = useCallback((nextVal) => {
    setCollapsed(v => {
      const next = typeof nextVal === 'boolean' ? nextVal : !v;
      try { localStorage.setItem(STORAGE_KEY_COLLAPSED, next ? '1' : '0'); } catch { /* quota */ }
      return next;
    });
  }, []);

  if (hidden) return null;

  // ChatPage 始终挂载，避免折叠/展开 / 路由切换时丢 state、重新拉 skills 列表。
  // 折叠时用 display:none 隐藏小窗 + 显示右下圆按钮即可。
  return (
    <>
      {/* 展开态浮动窗口（collapsed 时 display:none） */}
      <div
        style={{
          position: 'fixed',
          left: pos.left,
          top: pos.top,
          width: WIN_WIDTH,
          height: WIN_HEIGHT,
          backgroundColor: 'var(--color-background, #0A0A0A)',
          border: '1px solid var(--color-borderStrong, #3D3D3D)',
          boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
          zIndex: 1000,
          display: collapsed ? 'none' : 'flex',
          flexDirection: 'column',
          userSelect: dragging ? 'none' : 'auto',
        }}
      >
        {/* 标题栏（拖拽 handle） */}
        <div
          onMouseDown={onDragStart}
          className="term-uimono"
          style={{
            height: 28,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '0 6px 0 10px',
            backgroundColor: 'var(--color-panelAlt, #161616)',
            borderBottom: '1px solid var(--color-border, #2A2A2A)',
            cursor: dragging ? 'grabbing' : 'grab',
            flexShrink: 0,
            fontSize: 11,
            letterSpacing: '0.08em',
            color: 'var(--color-accent, #FF7A00)',
          }}
        >
          <MessageSquare size={12} />
          <span style={{ fontWeight: 700 }}>SAGAX AGENT</span>
          <span className="term-muted" style={{ fontSize: 10, fontWeight: 400 }}>· F1</span>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            onClick={() => navigate('/sagax-agent')}
            title="在全屏页面打开"
            style={agentBtnStyle}
          >
            <Maximize2 size={13} />
          </button>
          <button
            type="button"
            onClick={() => toggleCollapsed(true)}
            title="最小化"
            style={agentBtnStyle}
          >
            <Minus size={13} />
          </button>
          <button
            type="button"
            onClick={() => toggleCollapsed(true)}
            title="关闭"
            style={agentBtnStyle}
          >
            <X size={13} />
          </button>
        </div>

        {/* Chat 内容 — embed 模式 */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Suspense fallback={
            <div className="px-2 py-2 term-muted" style={{ fontSize: 11 }}>LOADING AGENT…</div>
          }>
            <ChatPage embed />
          </Suspense>
        </div>
      </div>

      {/* 折叠态：右下浮动圆按钮 */}
      {collapsed && (
        <button
          type="button"
          onClick={() => toggleCollapsed(false)}
          title="打开 SAGAX Agent"
          style={{
            position: 'fixed',
            right: MARGIN,
            bottom: 80,
            width: FAB_SIZE,
            height: FAB_SIZE,
            borderRadius: '50%',
            backgroundColor: 'var(--color-accent, #FF7A00)',
            color: '#000',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <MessageSquare size={20} />
        </button>
      )}
    </>
  );
};

const agentBtnStyle = {
  background: 'none',
  border: 'none',
  color: 'var(--color-textMuted, #9A9A9A)',
  cursor: 'pointer',
  padding: 4,
  display: 'flex',
  alignItems: 'center',
  borderRadius: 2,
};

export default AgentFloatingWindow;
